"use client";
import { Input } from "@nextui-org/input";
import { useRouter } from "next13-progressbar";
import { useState, useEffect } from "react";
import {
  IoSearchOutline,
  IoPersonOutline,
  IoDocumentTextOutline,
  IoBusinessOutline,
  IoCalendarOutline,
  IoBulbOutline,
  IoHammerOutline,
  IoSettingsOutline,
} from "react-icons/io5";

import { callSolrSearch } from "@/app/components/_actions";

function SearchBox() {
  const options = [
    { id: 1, label: "Autores", name: "pref_name" },
    { id: 2, label: "Documentos", name: "title" },
    { id: 3, label: "Organizaciones", name: "alias" },
    { id: 4, label: "Eventos", name: "name" },
    { id: 5, label: "Proyectos", name: "title" },
    { id: 6, label: "Patentes", name: "title" },
    { id: 7, label: "Servicios", name: "name" },
  ];

  const [optionSelected, setOptionSelected] = useState(options[0]);
  const [searchInput, setSearchInput] = useState("");
  const [searchResults, setSearchResults] = useState([]); // Estado para los resultados de búsqueda
  const [showResults, setShowResults] = useState(false); // Controlar la visibilidad de los resultados
  const router = useRouter();

  const fetchResults = async (query) => {
    const results = await callSolrSearch(query, optionSelected.id, 1, []);

    const docs = results.response.docs;
    return docs.map((doc) => {
      return {
        id: doc.id,
        name: doc[optionSelected.name],
      };
    });
  };

  // Manejar la búsqueda en tiempo real
  useEffect(() => {
    if (searchInput !== "") {
      const timer = setTimeout(async () => {
        const results = await fetchResults(searchInput);
        setSearchResults(results);
        setShowResults(true); // Mostrar resultados
      }, 300); // Debouncing (esperar 300ms antes de hacer la búsqueda)
      return () => clearTimeout(timer);
    } else {
      setSearchResults([]);
      setShowResults(false); // Ocultar resultados si no hay búsqueda
    }
  }, [searchInput, optionSelected]); // Añadido `optionSelected` para que se haga la búsqueda cuando cambie la opción seleccionada

  // Resetear la búsqueda cuando se cambia la opción seleccionada
  function handleSetSelectedOption(option) {
    setOptionSelected(option);
    setSearchInput(""); // Resetear el campo de búsqueda
    setSearchResults([]); // Limpiar los resultados de búsqueda
    setShowResults(false); // Ocultar los resultados
  }

  function handleInputChange(event) {
    setSearchInput(event.target.value);
  }

  function handleResultClick(result) {
    setSearchInput(result.name); // Mostrar el nombre del resultado en el input
    setShowResults(false); // Ocultar resultados
    const url =
      result.name === ""
        ? `/search?o=${optionSelected.id}`
        : `/search?q=${result.name}&o=${optionSelected.id}`;
    router.push(url); // Redirigir
  }

  function handlePress(event) {
    if (event.key === "Enter") handleResultClick({ name: searchInput });
  }
  // Función para seleccionar un ícono diferente basado en la opción seleccionada
  const getIconForResult = (optionId) => {
    switch (optionId) {
      case 1:
        return <IoPersonOutline className="text-gray-900" size={24} />;
      case 2:
        return <IoDocumentTextOutline className="text-gray-900" size={24} />;
      case 3:
        return <IoBusinessOutline className="text-gray-900" size={24} />;
      case 4:
        return <IoCalendarOutline className="text-gray-900" size={24} />;
      case 5:
        return <IoBulbOutline className="text-gray-900" size={24} />;
      case 6:
        return <IoHammerOutline className="text-gray-900" size={24} />;
      case 7:
        return <IoSettingsOutline className="text-gray-900" size={24} />;
      default:
        return <IoSearchOutline className="text-gray-900" size={24} />;
    }
  };

  return (
    <div className="relative w-full pt-0 pb-5 rounded-3xl shadow-lg px-4 sm:px-10">
      <div className="flex flex-row py-4 gap-4 font-bold flex-wrap justify-center">
        {options.map((option) => (
          <div
            key={option.id}
            className={`px-3 hover:cursor-pointer hover:bg-white hover:text-blue-800 hover:rounded-lg ${
              optionSelected.id === option.id ? "border-b" : "border-none"
            } `}
            onClick={() => handleSetSelectedOption(option)}
          >
            {option.label}
          </div>
        ))}
      </div>
      <div className="relative w-full">
        <Input
          type="text"
          width="100%"
          radius="full"
          value={searchInput}
          onChange={handleInputChange}
          placeholder={`Buscar ${optionSelected.label.toLowerCase()}...`}
          labelPlacement="outside"
          classNames={{
            input: ["caret-gray-500 h-auto"],
          }}
          onKeyUp={handlePress}
          endContent={
            <IoSearchOutline
              className="text-2xl font-bold hover:cursor-pointer text-gray-900"
              onClick={() => handleResultClick({ name: searchInput })}
            >
              search
            </IoSearchOutline>
          }
        />

        {/* Lista de resultados */}
        {showResults && (
          <ul
            className="absolute bg-white border border-gray-200 mt-2 rounded-lg shadow-lg z-50 max-h-60 overflow-y-auto text-gray-800 text-left w-full"
            style={{ zIndex: 1000 }}
          >
            {searchResults.length > 0 ? (
              searchResults.map((result, index) => (
                <li
                  key={result.id}
                  className={`px-4 py-2 hover:bg-gray-100 cursor-pointer flex items-start sm:items-center w-full ${
                    index !== searchResults.length - 1
                      ? "border-b border-gray-200"
                      : ""
                  }`}
                  onClick={() => handleResultClick(result)}
                >
                  <div
                    className="mr-2 flex-shrink-0"
                    style={{ width: "24px", height: "24px" }}
                  >
                    {getIconForResult(optionSelected.id)}
                  </div>
                  <span className="break-words">{result.name}</span>
                </li>
              ))
            ) : (
              <li className="px-4 py-2 text-gray-800 w-full">
                No se encontraron resultados
              </li>
            )}
          </ul>
        )}
      </div>
    </div>
  );
}

export default SearchBox;
